import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

const inputVariants = cva(
  cn(
    'flex w-full text-sm lg:text-base transition-colors file:border-0 file:bg-transparent file:text-sm placeholder:text-[#646B96] text-secondary relative',
    'hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
    'border-box items-center gap-2',
  ),
  {
    variants: {
      variant: {
        transparent: ['bg-transparent', 'ea-input__transparent'],
        filled: ['bg-[#F3F4F6]', 'ea-input__filled'],
      },

      rounded: {
        default: ['rounded-[12px]', 'ea-input__wrapper--rounded-default'],
        none: ['rounded-none', 'ea-input__wrapper--rounded-none'],
      },
      border: {
        bordered: ['border', 'ea-input__wrapper-border-bordered'],
        none: ['border-none', 'ea-input__wrapper-border-none'],
      },
    },

    defaultVariants: {
      rounded: 'default',
      variant: 'transparent',
      border: 'bordered',
    },
  },
);
export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>,
    VariantProps<typeof inputVariants> {
  before?: React.ReactNode;
  after?: React.ReactNode;
  floatingLabel?: boolean;
  beforeOuter?: React.ReactNode;
  afterOuter?: React.ReactNode;

  className?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
  classNames?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      before,
      after,
      afterOuter,
      beforeOuter,
      classNames,
      floatingLabel,
      ...props
    },
    ref,
  ) => {
    return (
      <>
        {beforeOuter}
        <div
          className={cn(
            inputVariants({
              rounded: props.rounded,
              variant: props.variant,
            }),
            'ea-input__wrapper',
            'h-12 sm:h-[52px]',
            props['aria-invalid'] &&
              'ea-input__wrapper--invalid border-destructive-foreground text-destructive-foreground',
            className?.wrapper,
            classNames?.wrapper,
          )}
        >
          <div
            className={cn(
              'absolute inset-y-0 left-0 flex h-full items-center pl-3',
              className?.before,
              classNames?.before,
            )}
          >
            {before}
          </div>
          <input
            type={type}
            className={cn(
              'h-12 w-full flex-1 overflow-hidden rounded-[12px] bg-transparent px-[18px] hover:outline-none focus:outline-none sm:h-[52px]',
              'text-secondary file:h-full file:cursor-pointer file:border-none file:border-input file:bg-primary file:outline-none placeholder:text-[#646B96] file:hover:bg-primary/90',
              !!before && 'pl-10',
              !!after && 'pr-10',
              'ea-input__main',
              className?.main,
              classNames?.main,
            )}
            ref={ref}
            {...props}
          />
          <div
            className={cn(
              'absolute inset-y-0 right-0 flex h-full items-center pr-3',
              className?.after,
              classNames?.after,
            )}
          >
            {after}
          </div>
        </div>
        {afterOuter}
      </>
    );
  },
);
Input.displayName = 'Input';

export { Input };
